import { Controller } from "@hotwired/stimulus"

// Highlights the section currently being read in the "On this page" list.
//
// The rule is deterministic: the active section is the LAST heading whose top has
// scrolled above a line just below the sticky header. Picking by "whatever is
// intersecting" is ambiguous whenever several headings share the viewport.
export default class extends Controller {
  static targets = ["link"]

  // Must stay >= the headings' scroll-margin-top (see application.tailwind.scss),
  // otherwise a heading jumped to via its anchor lands below the line and the
  // previous section stays highlighted.
  static OFFSET_BELOW_HEADER = 40

  connect() {
    this.requestUpdate = this.requestUpdate.bind(this)

    window.addEventListener("scroll", this.requestUpdate, { passive: true })
    window.addEventListener("resize", this.requestUpdate, { passive: true })

    this.update()
  }

  disconnect() {
    window.removeEventListener("scroll", this.requestUpdate)
    window.removeEventListener("resize", this.requestUpdate)
    if (this.frame) cancelAnimationFrame(this.frame)
  }

  requestUpdate() {
    if (this.frame) return
    this.frame = requestAnimationFrame(() => {
      this.frame = null
      this.update()
    })
  }

  // Headings in document order, limited to those the TOC actually links to.
  get sections() {
    const linked = new Set(this.linkTargets.map((link) => link.dataset.section))
    return [ ...document.querySelectorAll("article h2[id], article h3[id], article h4[id]") ]
      .filter((heading) => linked.has(heading.id))
  }

  get threshold() {
    const header = document.querySelector("header")
    const headerHeight = header ? header.getBoundingClientRect().height : 0
    return headerHeight + this.constructor.OFFSET_BELOW_HEADER
  }

  get scrolledToBottom() {
    return window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2
  }

  update() {
    const sections = this.sections
    if (!sections.length) return

    // The final sections are often too short to ever cross the line, so they would
    // never highlight without this.
    if (this.scrolledToBottom) {
      this.setActiveLink(sections[sections.length - 1].id)
      return
    }

    let current = sections[0]
    for (const section of sections) {
      if (section.getBoundingClientRect().top > this.threshold) break
      current = section
    }

    this.setActiveLink(current.id)
  }

  setActiveLink(sectionId) {
    if (sectionId === this.activeId) return
    this.activeId = sectionId

    this.linkTargets.forEach((link) => {
      const active = link.dataset.section === sectionId
      link.classList.toggle("text-primary", active)
      link.classList.toggle("font-semibold", active)
      link.classList.toggle("border-primary", active)
      link.classList.toggle("text-gray-600", !active)
      link.classList.toggle("border-transparent", !active)
    })
  }
}
